/**
 * Gamified journey layout for onboarding steps
 * Displays current level and completed steps
 */

import React, { useEffect, useRef, useState } from "react";
import { Alert, Flex, Stack } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type { OnboardingStepItem } from "../types";
import LevelSection from "./LevelSection";
import JourneyConnector from "./JourneyConnector";
import OnboardingStepSkeleton from "./OnboardingStepSkeleton";
import CompletedStepsSection from "./CompletedStepsSection";
import WelcomeHeader from "./WelcomeHeader";
import SettingsCardsGrid from "./SettingsCardsGrid";
import { type LevelType } from "../utils/levelUtils";

interface OnboardingStepsGridProps {
  steps: OnboardingStepItem[];
  onStepClick: (stepId: string) => void;
  loading?: boolean;
  error?: string | null;
  loadingStepId?: string | null;
}

/**
 * Main grid component for displaying all onboarding steps
 * Handles loading states, errors, and responsive layout
 */
const OnboardingStepsGrid: React.FC<OnboardingStepsGridProps> = ({
  steps,
  onStepClick,
  loading = false,
  error = null,
  loadingStepId = null,
}) => {
  // Ref for scrolling to completed section (must be at top before any early returns)
  const completedSectionRef = useRef<HTMLDivElement>(null);

  // State for completed steps collapsed/expanded
  const [isCompletedCollapsed, setIsCompletedCollapsed] = useState(false);

  // Initialize collapsed state when all steps are complete
  useEffect(() => {
    // Calculate if all steps are complete
    const basicSteps = steps.filter((step) => step.category === "basic");
    const advancedSteps = steps.filter((step) => step.category === "advanced");
    const eliteSteps = steps.filter((step) => step.category === "elite");

    const isBasicComplete =
      basicSteps.length > 0 &&
      basicSteps.every((step) => step.status === "complete");
    const isAdvancedComplete =
      advancedSteps.length > 0 &&
      advancedSteps.every((step) => step.status === "complete");
    const isEliteComplete =
      eliteSteps.length > 0 &&
      eliteSteps.every((step) => step.status === "complete");

    const completedLevels = [];
    if (isBasicComplete) completedLevels.push("basic");
    if (isAdvancedComplete) completedLevels.push("advanced");
    if (isEliteComplete) completedLevels.push("elite");

    const currentLevel =
      !isBasicComplete && basicSteps.length > 0
        ? "basic"
        : !isAdvancedComplete && advancedSteps.length > 0
          ? "advanced"
          : !isEliteComplete && eliteSteps.length > 0
            ? "elite"
            : null;

    const allStepsComplete = !currentLevel && completedLevels.length > 0;

    if (allStepsComplete) {
      setIsCompletedCollapsed(true);
    }
  }, [steps]);

  // Scroll to completed section
  const scrollToCompleted = () => {
    completedSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Function to toggle and scroll to completed section
  const handleViewOnboardingSteps = () => {
    if (isCompletedCollapsed) {
      // If collapsed, expand and scroll
      setIsCompletedCollapsed(false);
      setTimeout(() => {
        scrollToCompleted(); // Scroll after expansion animation
      }, 100);
    } else {
      // If expanded, just collapse (no scroll needed)
      setIsCompletedCollapsed(true);
    }
  };

  // Show loading state with skeleton cards
  if (loading) {
    return (
      <Stack gap="xl">
        {/* Header Section - Show actual content */}
        <WelcomeHeader order={1} mb="md" />

        {/* Skeleton Cards Grid */}
        <Flex wrap="wrap" gap="xl" justify="center">
          {Array.from({ length: 4 }).map((_, index) => (
            <OnboardingStepSkeleton key={index} />
          ))}
        </Flex>
      </Stack>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={20} />}
        title="Error Loading Steps"
        color="red"
        variant="light"
        radius="md"
      >
        {error}
      </Alert>
    );
  }

  // Separate steps by category
  const basicSteps = steps.filter((step) => step.category === "basic");
  const advancedSteps = steps.filter((step) => step.category === "advanced");
  const eliteSteps = steps.filter((step) => step.category === "elite");

  // Check completion status for each level
  const isBasicComplete =
    basicSteps.length > 0 &&
    basicSteps.every((step) => step.status === "complete");

  const isAdvancedComplete =
    advancedSteps.length > 0 &&
    advancedSteps.every((step) => step.status === "complete");

  const isEliteComplete =
    eliteSteps.length > 0 &&
    eliteSteps.every((step) => step.status === "complete");

  // Determine completed levels
  const completedLevels: LevelType[] = [];
  if (isBasicComplete) completedLevels.push("basic");
  if (isAdvancedComplete) completedLevels.push("advanced");
  if (isEliteComplete) completedLevels.push("elite");

  // Get all completed steps
  const completedSteps = steps.filter((step) => step.status === "complete");

  // Determine current level (first level that's not complete)
  let currentLevel: LevelType | null = null;
  let currentLevelSteps: OnboardingStepItem[] = [];

  if (!isBasicComplete && basicSteps.length > 0) {
    currentLevel = "basic";
    currentLevelSteps = basicSteps;
  } else if (!isAdvancedComplete && advancedSteps.length > 0) {
    currentLevel = "advanced";
    currentLevelSteps = advancedSteps;
  } else if (!isEliteComplete && eliteSteps.length > 0) {
    currentLevel = "elite";
    currentLevelSteps = eliteSteps;
  }

  // Check if all steps are complete
  const allStepsComplete = !currentLevel && completedLevels.length > 0;

  return (
    <Stack gap={0}>
      {/* Header Section */}
      <WelcomeHeader allStepsComplete={allStepsComplete} />

      {/* Settings Cards Grid (only show when all steps are complete) */}
      {allStepsComplete && (
        <SettingsCardsGrid
          onViewOnboardingSteps={handleViewOnboardingSteps}
          isOnboardingCollapsed={isCompletedCollapsed}
        />
      )}

      {/* Current Level Section (only show if there's an active level) */}
      {currentLevel && (
        <LevelSection
          level={currentLevel}
          steps={currentLevelSteps}
          isLocked={false}
          onStepClick={onStepClick}
          loadingStepId={loadingStepId}
        />
      )}

      {/* Connector to Completed Section - Clickable (hide when all steps complete) */}
      {completedSteps.length > 0 && currentLevel && (
        <JourneyConnector
          isUnlocked={true}
          color="#51cf66"
          onClick={scrollToCompleted}
        />
      )}

      {/* Completed Steps Section */}
      <div ref={completedSectionRef}>
        <CompletedStepsSection
          completedSteps={completedSteps}
          onStepClick={onStepClick}
          showHeader={currentLevel !== null}
          allStepsComplete={currentLevel === null && completedSteps.length > 0}
          isCollapsed={isCompletedCollapsed}
          setIsCollapsed={setIsCompletedCollapsed}
        />
      </div>
    </Stack>
  );
};

export default OnboardingStepsGrid;
